import React, { useCallback, useEffect, useRef, useState } from "react";
import { IconButton, Paper, Typography, makeStyles } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import clsx from "clsx";

import api from "../../services/api";
import openSocket from "../../services/socket-io";
import { i18n } from "../../translate/i18n";

const VISIBLE_MS = 3000;
const FADE_MS = 700;

const useStyles = makeStyles(theme => ({
  box: {
    position: "absolute",
    right: 16,
    top: 88,
    bottom: "auto",
    zIndex: 20,
    width: 280,
    maxWidth: "calc(100% - 32px)",
    padding: "12px 12px 10px",
    borderRadius: 12,
    boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
    background: theme.palette.background.paper,
    opacity: 1,
    transform: "translateY(0)",
    transition: `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`,
    pointerEvents: "auto"
  },
  fading: {
    opacity: 0,
    transform: "translateY(-8px)",
    pointerEvents: "none"
  },
  head: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8
  },
  titleWrap: {
    minWidth: 0,
    flex: 1
  },
  title: {
    fontWeight: 700,
    fontSize: "0.85rem",
    lineHeight: 1.3
  },
  status: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: 4,
    fontSize: "0.78rem",
    fontWeight: 600
  },
  node: {
    marginTop: 6,
    color: theme.palette.text.secondary,
    fontSize: "0.75rem"
  },
  warn: {
    marginTop: 8,
    padding: "6px 8px",
    borderRadius: 8,
    background: "rgba(211,47,47,0.08)",
    color: theme.palette.error.main,
    fontSize: "0.72rem",
    lineHeight: 1.35
  },
  iconOk: { fontSize: 18, color: theme.palette.primary.main },
  iconWait: { fontSize: 18, color: theme.palette.warning.main },
  iconRun: { fontSize: 18, color: theme.palette.primary.main },
  iconErr: { fontSize: 18, color: theme.palette.error.main },
  close: {
    margin: -8,
    padding: 4
  }
}));

const isTerminal = session =>
  Boolean(
    session?.completed ||
      session?.status === "completed" ||
      session?.status === "handed_off" ||
      session?.status === "cancelled" ||
      session?.status === "error"
  );

const resolveMeta = session => {
  const waiting =
    session.status === "active" &&
    (session.waitingFor === "reply" ||
      session.waitingFor === "input" ||
      session.waitingFor === "button" ||
      session.waitingFor === "list" ||
      session.waitingFor === "delay");

  if (session.status === "completed" || session.completed) {
    return {
      iconClass: "iconOk",
      Icon: CheckCircleIcon,
      label: i18n.t("messagesInput.chatFlows.statusCompleted")
    };
  }
  if (session.status === "handed_off") {
    return {
      iconClass: "iconOk",
      Icon: CheckCircleIcon,
      label: i18n.t("messagesInput.chatFlows.statusHandedOff")
    };
  }
  if (session.status === "error") {
    return {
      iconClass: "iconErr",
      Icon: ErrorOutlineIcon,
      label: i18n.t("messagesInput.chatFlows.statusError")
    };
  }
  if (session.status === "cancelled") {
    return {
      iconClass: "iconErr",
      Icon: ErrorOutlineIcon,
      label: i18n.t("messagesInput.chatFlows.statusCancelled")
    };
  }
  if (waiting) {
    return {
      iconClass: "iconWait",
      Icon: HourglassEmptyIcon,
      label: i18n.t("messagesInput.chatFlows.statusWaiting")
    };
  }
  return {
    iconClass: "iconRun",
    Icon: PlayArrowIcon,
    label: i18n.t("messagesInput.chatFlows.statusRunning")
  };
};

const sendErrorText = code => {
  if (!code) return null;
  const key = `backendErrors.${code}`;
  if (i18n.exists(key)) return i18n.t(key);
  return i18n.t("backendErrors.ERR_SENDING_WAPP_MSG");
};

const TicketChatFlowStatus = ({ ticketId }) => {
  const classes = useStyles();
  const [session, setSession] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [fading, setFading] = useState(false);
  const fadeTimer = useRef(null);
  const hideTimer = useRef(null);

  const clearTimers = useCallback(() => {
    if (fadeTimer.current) {
      clearTimeout(fadeTimer.current);
      fadeTimer.current = null;
    }
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  }, []);

  const reveal = useCallback(
    next => {
      if (!next) {
        setSession(null);
        setHidden(true);
        return;
      }
      setSession(next);
      setHidden(false);
      setFading(false);
      clearTimers();
      fadeTimer.current = setTimeout(() => {
        setFading(true);
        hideTimer.current = setTimeout(() => {
          setHidden(true);
        }, FADE_MS);
      }, VISIBLE_MS);
    },
    [clearTimers]
  );

  const loadSession = useCallback(async () => {
    if (!ticketId) {
      setSession(null);
      return;
    }
    try {
      const { data } = await api.get(`/chat-flows/ticket/${ticketId}/session`);
      if (data && !isTerminal(data)) {
        reveal(data);
      } else {
        setSession(null);
      }
    } catch (err) {
      setSession(null);
    }
  }, [ticketId, reveal]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  useEffect(() => {
    if (!ticketId) return undefined;
    const socket = openSocket();
    const handler = data => {
      if (Number(data?.ticketId) !== Number(ticketId)) return;
      if (data?.session) reveal(data.session);
    };
    socket.on("chatFlowSession", handler);
    return () => {
      socket.off("chatFlowSession", handler);
    };
  }, [ticketId, reveal]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  if (!session || hidden) return null;

  const meta = resolveMeta(session);
  const StatusIcon = meta.Icon;
  const warn = sendErrorText(session.lastSendError);

  return (
    <Paper
      className={clsx(classes.box, fading && classes.fading)}
      elevation={6}
    >
      <div className={classes.head}>
        <div className={classes.titleWrap}>
          <Typography className={classes.title} noWrap>
            {session.flowName}
          </Typography>
          <Typography className={classes.status} component="div">
            <StatusIcon className={classes[meta.iconClass]} />
            {meta.label}
          </Typography>
        </div>
        <IconButton
          size="small"
          className={classes.close}
          aria-label="fechar"
          onClick={() => {
            clearTimers();
            setHidden(true);
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
      {session.currentNodeLabel ? (
        <Typography className={classes.node}>
          {session.currentNodeLabel}
        </Typography>
      ) : null}
      {warn ? <div className={classes.warn}>{warn}</div> : null}
    </Paper>
  );
};

export default TicketChatFlowStatus;
